import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EligibleItem, j5m3Api } from '../../services/j5m3Api';
import { useJ5M3Reversal } from './hooks/useJ5M3Reversal';
import { BatchAcceptanceBridge, pwj5m3f1AcceptanceManifest } from '../../features/acceptance';
import './J5M3Form.css';

const { Title, Text } = Typography;

function renderStatus(status: string): React.ReactNode {
  if (status === 'P') return <Tag color="blue">已核准</Tag>;
  if (status === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="default">{status || '未知'}</Tag>;
}

const J5M3Form: React.FC = () => {
  const {
    loading,
    executing,
    items,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchEligible,
    batchExecute,
  } = useJ5M3Reversal();

  const columns: ColumnsType<EligibleItem> = [
    {
      title: '交運單號',
      dataIndex: 'fj130_01',
      key: 'fj130_01',
      width: 110,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '日期',
      dataIndex: 'fj130_11',
      key: 'fj130_11',
      width: 110,
      render: (date: string) => date || '\u2014',
    },
    {
      title: '客戶',
      dataIndex: 'fj130_12',
      key: 'fj130_12',
      width: 140,
      ellipsis: true,
    },
    {
      title: '產品',
      dataIndex: 'fj130_21',
      key: 'fj130_21',
      width: 120,
    },
    {
      title: '色號',
      dataIndex: 'fj130_22',
      key: 'fj130_22',
      width: 80,
    },
    {
      title: '狀態',
      dataIndex: 'fj130_80',
      key: 'fj130_80',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  const handleSearch = () => { fetchEligible(); };

  const handleFilterChange = (field: 'startNo' | 'endNo', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  const handleStartBlur = () => {
    if (filter.startNo && !filter.endNo) {
      const autoEnd = (filter.startNo + 'zzzzzzz').substring(0, 7);
      setFilter({ ...filter, endNo: autoEnd });
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>樣品交運單核准沖銷 (pwj5m3f1)</Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label="單號起始">
            <Input
              data-contract-field="fj130_01_start"
              value={filter.startNo}
              onChange={(e) => handleFilterChange('startNo', e.target.value)}
              onBlur={handleStartBlur}
              placeholder="例：FJ00001"
              style={{ width: 120 }}
              maxLength={7}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label="單號結束">
            <Input
              data-contract-field="fj130_01_end"
              value={filter.endNo}
              onChange={(e) => handleFilterChange('endNo', e.target.value)}
              placeholder="例：FJ99999"
              style={{ width: 120 }}
              maxLength={7}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆樣品交運單？沖銷後將回復庫存，無法還原。`}
              onConfirm={batchExecute}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={executing}
                disabled={selectedKeys.length === 0}
              >
                批量沖銷
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      <Table<EligibleItem>
        rowKey="fj130_01"
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 700 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
      <BatchAcceptanceBridge
        manifest={pwj5m3f1AcceptanceManifest}
        records={items}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.fj130_01}
        queryExact={(key) => j5m3Api.search({ startNo: key, endNo: key })}
        lastApiResponse={items}
        loading={loading || executing}
        onAutoFill={() => items[0] && setSelectedKeys([items[0].fj130_01])}
        autoFillDisabled={items.length === 0}
        screenDraft={filter}
      />
    </div>
  );
};

export default J5M3Form;
